'use client';

import { useEffect, useState, type JSX } from 'react';
import { useSelector } from 'react-redux';
import { Container } from '@geberit/gdds';

// styles
import styles from '../press-portal.module.scss';

// types
import type { MediaType, Pressroom, Media, Material } from '../types/mynewsdesk.types';
import { Tabs } from '../types/press-portal.types';

// components
import PressTiles from '../press-tiles/press-tiles';
import { FilterSearchBar } from '../filtersearch-bar/filter-search-bar';
import { NavigationTabs } from '../navigation-tabs/navigation-tabs';
import { GDDSLoadmoreButton } from '../loadmore-button/gdds-loadmore-button';
import { NoResults } from '../no-result/no-result';
import ContactTiles from '../contact-tiles/contact-tiles';
import MediaTiles from '../media-tiles/media-tiles';

// utils
import { useThemeName } from 'utils/hooks/use-theme';
import { buildSize, gridSizes } from 'utils/gridSize';
import { useIsoLang } from 'scenes/MetaData';
import { monochromeLogoSelector } from 'utils/selectors/globalsSelectors';
import { isServer } from 'isServer';
import { useFetchMaterials } from '../hooks/use-fetch-materials';
import { MATERIAL_TYPES } from '../constants';

const skipFirst = (_: { id: string; value: string; text: string }, i: number): boolean => i > 0;

const getFallbackBackgroundColor: Record<string, string> = {
  DEFAULT: '#376eb4',
  XY: '#376eb4',
  TWYFORD: '#245c4e',
  NORD: '#26513b',
  KOLO: '#1e818a',
};

type PressPortalPageProps = {
  isGdds: boolean;
  initialMaterials: Material[];
  initialTab: Tabs;
  info?: Record<MediaType, { done: boolean; offset: number; }>;
  pressroom?: Pressroom | null;
};

function PressPortalPage({ isGdds, initialMaterials, initialTab, info, pressroom }: Readonly<PressPortalPageProps>): JSX.Element | null {
  const [types, setTypes] = useState<{ mediaType?: MediaType; mediaTypes?: MediaType[] }>();
  const [selectedTab, setSelectedTab] = useState<Tabs | undefined>();
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState<string | undefined>();
  const themeName = useThemeName();
  const language = useIsoLang();
  const monochromeLogo = useSelector(monochromeLogoSelector);

  useEffect(() => {
    setTypes({
      mediaType: undefined,
      mediaTypes:
        selectedTab === undefined
          ? undefined
          : MATERIAL_TYPES[selectedTab]?.filter(skipFirst).map((m) => m.value as MediaType),
    });
  }, [selectedTab]);

  const itemsPerPage = 12;

  const { materials, loadMore, loadable, loading } = useFetchMaterials({
    mediaType: types?.mediaType,
    mediaTypes: types?.mediaTypes,
    search: query,
    limit: (iteration) => ((iteration === 0) && (selectedTab === Tabs.News)) ? 13 : 12,
    offset,
    locale: language.toLowerCase(),
    initialItems: initialMaterials,
    info,
  });

  function getMediaTypeChangehandler() {
    return (option) => {
      if (!Array.isArray(option)) {
        setTypes((types) => ({
          ...types,
          mediaType: option.value === 'all' ? undefined : option.value,
        }));
      }
    };
  }

  function getSearchChangehandler() {
    return (term?: string) => {
      setOffset(0);
      setQuery(term);
    };
  }

  function renderMaterials() {
    const materialsList = isServer ? initialMaterials.slice(0, 12) : materials;

    if (!materialsList || materialsList?.length === 0) {
      return null;
    }

    if (!query && (!pressroom?.tag_cloud || pressroom?.tag_cloud?.length === 0)) {
      return null;
    }

    if ((selectedTab === Tabs.News) || (isServer && (initialTab === Tabs.News))) {
      return (
        <PressTiles
          language={language}
          materials={materialsList as Material[]}
          fallbackImg={monochromeLogo?.logoRef}
          fallbackBackgroundColor={getFallbackBackgroundColor[themeName]}
        />
      );
    }

    if ((selectedTab === Tabs.MediaLibrary) || (isServer && (initialTab === Tabs.MediaLibrary))) {
      return (
        <MediaTiles
          language={language}
          mediaList={materialsList as Media[]}
          fallbackImg={monochromeLogo?.logoRef}
          fallbackBackgroundColor={getFallbackBackgroundColor[themeName]}
        />
      );
    }

    return null;
  }

  return (
    <section className={styles.pressPortal}>
      <div className={styles.feedSection}>
        <NavigationTabs onChange={setSelectedTab} />
        <Container maxContentWidth={isGdds ? buildSize(gridSizes.gddsFullGrid) : '80rem'}>
          <FilterSearchBar
            materialType={types?.mediaType}
            query={query}
            getSearchChangehandler={getSearchChangehandler()}
            materialTypes={MATERIAL_TYPES[selectedTab || Tabs.News]}
            getMediaTypeChangehandler={getMediaTypeChangehandler()}
          />
          {renderMaterials() || <NoResults loading={loading} />}
          {loadable && materials && materials?.length >= itemsPerPage && (
            <GDDSLoadmoreButton
              onClick={() => {
                loadMore();
                setOffset((prev) => prev + 1);
              }}
              disabled={loading}
            />
          )}
        </Container>
      </div>
      <Container {...(!isGdds && { maxContentWidth: '80rem' })}>
        {pressroom?.presscontacts && (
          <ContactTiles
            withDivider={false}
            language={language}
            contacts={pressroom?.presscontacts}
          />
        )}
      </Container>
    </section>
  );
}

export default PressPortalPage;
